import React, { useState, useEffect } from 'react';
import { Play, X, Maximize2 } from 'lucide-react';

const VideoModal = ({ video, onClose }) => {
  // Close on ESC key press
  useEffect(() => {
    if (!video) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, video]);

  if (!video) return null;

  // Phones/tablets get the lighter version when one exists; desktop keeps the original
  const isSmallScreen = typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches;
  const src = (isSmallScreen && video.mobileVideoUrl) || video.videoUrl;

  // Close when clicking on backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      onClick={handleBackdropClick}
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 overflow-y-auto"
    >
      {/* Close Button - Fixed to viewport, always visible */}
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] bg-red-600 hover:bg-red-700 text-white rounded-full p-3 transition-all duration-300 shadow-lg hover:scale-110 flex items-center gap-2"
        aria-label="Close video"
        data-testid="video-modal-close-button"
      >
        <X size={24} />
        <span className="text-sm font-semibold pr-1">Close</span>
      </button>

      <div className="min-h-full flex items-center justify-center p-4 pt-20 md:pt-16">
        <div className="relative w-full max-w-6xl my-4">
        {/* Video Player */}
        <div className="relative aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
          <video
            key={src}
            controls
            autoPlay
            playsInline
            preload="metadata"
            poster={video.thumbnail}
            className="w-full h-full object-contain"
            controlsList="nodownload"
            data-testid="video-modal-player"
            onClick={(e) => e.stopPropagation()}
          >
            <source src={src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Video Info */}
        <div className="mt-4 text-center bg-gray-900/50 backdrop-blur-sm rounded-lg p-4 border border-gray-700">
          <h3 className="text-2xl font-bold text-white mb-1">{video.title}</h3>
          {video.subtitle && (
            <p className="text-sm uppercase tracking-[0.25em] text-amber-400 mb-2">{video.subtitle}</p>
          )}
          <p className="text-gray-400">{video.description}</p>
          {video.credit && <p className="text-xs italic text-amber-400/80 mt-2" data-testid="video-modal-credit">{video.credit}</p>}
          <p className="text-sm text-gray-500 mt-2">Click outside, press ESC, or click the red Close button to exit</p>
        </div>

        {/* Supporting stills from the presentation (only shown if provided) */}
        {Array.isArray(video.stills) && video.stills.length > 0 && (
          <div className="mt-4 bg-gray-900/50 backdrop-blur-sm rounded-lg p-4 border border-gray-700" data-testid="video-modal-stills">
            <p className="text-xs uppercase tracking-[0.25em] text-amber-400 mb-3 text-center">Stills from the presentation</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {video.stills.map((src, i) => (
                <div key={src} className="relative aspect-video rounded-md overflow-hidden border border-white/10 bg-black">
                  <img
                    src={src}
                    alt={`${video.title} still ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

const VideoCard = ({ video, onClick }) => {
  return (
    <div
      onClick={onClick}
      data-testid={`sample-card-${video.id}`}
      className="group bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl overflow-hidden border border-gray-700 hover:border-cyan-500 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2 cursor-pointer"
    >
      <div className="relative aspect-video overflow-hidden">
        <img 
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <button className="w-16 h-16 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300">
            <Play size={24} className="text-black ml-1" fill="black" />
          </button>
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-3 py-1 bg-black/70 backdrop-blur-sm text-white text-xs font-medium rounded-full border border-white/20">
            {video.category.charAt(0).toUpperCase() + video.category.slice(1)}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
          {video.title}
        </h3>
        <p className="text-gray-400 leading-relaxed">
          {video.description}
        </p>
        {video.credit && <p className="text-xs italic text-amber-400/80 mt-3">{video.credit}</p>}
      </div>
    </div>
  );
};

export { VideoModal, VideoCard };
